import { ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AGE_VERIFICATION_SKIP_PATHS, EXTERNAL_URLS } from '../lib/constants';

const AGE_VERIFIED_KEY = 'age_verified_at';
const VERIFICATION_EXPIRY_DAYS = 30;

export default function AgeVerificationGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation('common');
  const { pathname } = useLocation();
  const [isVerified, setIsVerified] = useState<boolean | null>(null);

  useEffect(() => {
    const isExempt = AGE_VERIFICATION_SKIP_PATHS.some(
      path => pathname === path || pathname.startsWith(`${path}/`),
    );
    if (isExempt) {
      setIsVerified(true);
      return;
    }

    const verifiedAt = localStorage.getItem(AGE_VERIFIED_KEY);
    if (verifiedAt) {
      const elapsed = Date.now() - new Date(verifiedAt).getTime();
      if (elapsed >= 0 && elapsed < VERIFICATION_EXPIRY_DAYS * 24 * 60 * 60 * 1000) {
        setIsVerified(true);
        return;
      }
      localStorage.removeItem(AGE_VERIFIED_KEY);
    }
    setIsVerified(false);
  }, [pathname]);

  const handleVerify = () => {
    localStorage.setItem(AGE_VERIFIED_KEY, new Date().toISOString());
    setIsVerified(true);
  };

  if (isVerified === null) return null;
  if (isVerified) return <>{children}</>;

  return (
    <main className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-900 p-4">
      <section className="w-full max-w-md rounded-xl bg-white p-8 text-center shadow-2xl" aria-labelledby="age-gate-title">
        <div className="mb-6">
          <img src="/favicon-192x192.png" alt="Queen's Live" className="mx-auto mb-4 h-20 w-20" />
          <h1 id="age-gate-title" className="mb-2 text-2xl font-bold text-gray-900">{t('ageVerification.title')}</h1>
          <p className="text-sm leading-relaxed text-gray-600">{t('ageVerification.description')}</p>
        </div>
        <div className="space-y-3">
          <button onClick={handleVerify} className="w-full rounded-lg bg-primary px-4 py-3 font-bold text-white shadow-md transition-all hover:bg-primary-600 hover:shadow-lg">
            {t('ageVerification.yes')}
          </button>
          <button onClick={() => { window.location.href = EXTERNAL_URLS.GOOGLE; }} className="w-full rounded-lg bg-gray-200 px-4 py-3 font-semibold text-gray-700 transition-all hover:bg-gray-300">
            {t('ageVerification.no')}
          </button>
        </div>
        <p className="mt-6 text-xs text-gray-500">{t('ageVerification.notice')}</p>
      </section>
    </main>
  );
}
